import { useState } from 'react'
import { CookieBar } from '../../components/CookieBar'
import { Footer } from '../../components/Footer'
import { Header } from '../../components/Header'
import { MediaItemsDetail } from '../../components/MediaMapDetail'
import { CommonPageDataFragment } from '../../data/CommonPageDataFragment'
import { combineUrl } from '../../data/combineUrl'
import { contember } from '../../utilities/contember'
import { getMediaItems } from '../../utilities/getMediaItem'
import type { InferDataLoaderProps } from '../../utilities/handlers'
import { handleGetStaticPaths, handleGetStaticProps } from '../../utilities/handlers'
import { createSlug } from '../../utilities/getMediaCategory'
import Head from 'next/head'

export type MediaMapPageProps = InferDataLoaderProps<typeof getStaticProps>

export default function MediaMapsPage(props: MediaMapPageProps) {
	const commonData = props.commonData
	const [isCookieBarOpen, setIsCookieBarOpen] = useState(false)

	const title = props.map.detail_o_webu?.nazev[0]
	const description = props.map.detail_o_webu?.popis?.[0]
	const screenshot = props.map.detail_o_webu?.screenshot?.[0]
	const canonicalUrl = `${process.env.NEXT_PUBLIC_WEB_URL ?? ''}/map/${props.slug}`
	const siteName = 'Nadační fond nezávislé žurnalistiky'

	return (
		<>
			{commonData.getHeaderLocale && (
				<Header
					header={commonData.getHeaderLocale}
					newsletterModal={commonData.getNewsletterFormLocale}
					parentPages={props.parentPages}
					resultPageUrl={props.commonData.getResultPage?.localesByLocale?.link?.url}
				/>
			)}
			<MediaItemsDetail itemSlug={props.map} />
			<Head>
				<title>{title}</title>
				{description && <meta name="description" content={description} />}
				<link rel="canonical" href={canonicalUrl} />

				{/* Open Graph / Facebook */}
				<meta property="og:type" content="article" />
				<meta property="og:url" content={canonicalUrl} />
				<meta property="og:title" content={title} />
				{description && <meta property="og:description" content={description} />}
				{screenshot && <meta property="og:image" content={screenshot} />}
				<meta property="og:site_name" content={siteName} />

				{/* Twitter Card */}
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:url" content={canonicalUrl} />
				<meta name="twitter:title" content={title} />
				{description && <meta name="twitter:description" content={description} />}
				{screenshot && <meta name="twitter:image" content={screenshot} />}
			</Head>
			{commonData.getCookieBarLocale && (
				<CookieBar
					cookieBar={commonData.getCookieBarLocale}
					isCookieBarOpen={isCookieBarOpen}
					setIsCookieBarOpen={setIsCookieBarOpen}
				/>
			)}
			{commonData.getFooterLocale && (
				<Footer
					footer={commonData.getFooterLocale}
					newsletterModal={commonData.getNewsletterFormLocale}
					openCookieBar={() => setIsCookieBarOpen(true)}
				/>
			)}
		</>
	)
}

export const getStaticPaths = handleGetStaticPaths(async () => {
	const mediaItemsPosts = await getMediaItems()

	const paths = mediaItemsPosts.map((post) => {
		return {
			params: {
				path: post.slug,
			},
		}
	})
	return {
		paths: paths,
		fallback: 'blocking',
	}
})

export const getStaticProps = handleGetStaticProps(async (context) => {
	const { locale } = combineUrl(context)

	if (typeof locale !== 'string') {
		throw new Error('Invalid locale')
	}

	const [commonData, mediaItems] = await Promise.all([contember.query(CommonPageDataFragment(locale)), getMediaItems()])

	if (!context.params?.path) {
		return { notFound: true, revalidate: 300 }
	}

	const slug = context.params.path

	if (mediaItems.length === 0) {
		return { notFound: true, revalidate: 300 }
	}

	const map = mediaItems.find((map) => {
		const detailSlug = createSlug(map.detail_o_webu.nazev[0])

		return detailSlug === slug
	})

	if (!map) {
		return { notFound: true, revalidate: 300 }
	}

	const detailSlug = createSlug(map.detail_o_webu.nazev[0])

	return {
		props: {
			commonData,
			map,
			slug: detailSlug,
			parentPages: commonData.getMediaMapPage?.page?.localesByLocale?.link?.url
				? [
						{
							url: commonData.getMediaMapPage?.page?.localesByLocale?.link?.url,
							title: commonData.getMediaMapPage?.page?.localesByLocale.title,
						},
				  ]
				: [],
		},
		revalidate: 300,
	}
})
